import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppConfig } from "../../context/context";
import { APP_LANGUAGES } from "../../utils/constants";
import { useUserService } from "../../services/user-service";
import pubSub, { EVENTS } from "../../config/pubSub";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from "@mui/material";
import { ArrowDropDownSharp, Info } from "@mui/icons-material";
import { styled } from "@mui/system";
import { IOrganizationModel } from "../../models/organizations.model";
import { useOrgService } from "../../services/organizations-service";

const TopNav = styled("div")({
 
  ".topNav": {
    height: "100%",
    backgroundColor: "transparent",
 
  },

  ".toolbar": {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  
    "& h3": {
      color: "var(--kyocera-dark-grey)",
    },
  },

  ".languageButton": {
    width: 110,
    margin: 10,
    color: "#6d6e71",
    textTransform: "none",
  },
});

const TopNavComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile, updateUserLocale, config } = useContext(AppConfig);
  const [userInitials, setUserInitials] = useState("")
  const [profileMenuEl, setProfileMenuEl] = useState<null | HTMLElement>(null);
  const [languageMenuEl, setLanguageMenuEl] = useState<null | HTMLElement>(
    null
  );
  const [userLanguage, setUserLanguage] = useState<string>("en");
  const [orgDisplayName, setOrgDisplayName] = useState<string>("");
  const { orgService } = useOrgService();
  const { userService } = useUserService();

  const initOrgDisplayName = useCallback(async () => {
    if (!orgService.getOrg) return;
    try {
      const org: IOrganizationModel = await orgService.getOrg(config.tenantId);
      setOrgDisplayName(org.displayName);
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  }, [orgService, config.tenantId]);

  useEffect(() => {
    initOrgDisplayName();
    if (config.language) {
      setUserLanguage(config.language);
    }
  }, [initOrgDisplayName, config.language]);

  useEffect(() => {
    const userInitials = userProfile.firstname?.substring(0, 1).toUpperCase() + userProfile.lastname?.substring(0, 1).toUpperCase()
    setUserInitials(userInitials ? userInitials : '') 
  }, [userProfile, config.language]);

  const handleUpdateUserLanguage = async (locale: string) => {
    const userSetting: any = {
      username: userProfile.username,
      email: userProfile.email,
      firstname: userProfile.firstname,
      lastname: userProfile.lastname,
      role: userProfile.role,
      locale: locale,
    };
    try {
      await userService.updateUserSetting(userProfile.id, userSetting);
      setUserLanguage(locale);
      updateUserLocale(locale);
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    } finally {
      setLanguageMenuEl(null);
    }
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProfileMenuEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileMenuEl(null);
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLanguageMenuEl(event.currentTarget);
  };

  const handleLanguageClose = async () => {
    setLanguageMenuEl(null);
  };

  const handleLogOut = () => {
    setProfileMenuEl(null);
    setLanguageMenuEl(null);
    navigate("/logout", { state: { from: window.location.href } });
  };

  const handleSettings = () => {
    setProfileMenuEl(null);
    navigate("/settings");
  };
  useEffect(() => {
    pubSub.subscribe(EVENTS.DISPLAY_NAME_CHANGED, (name: string) => {
      setOrgDisplayName(name);
    });
  }, []);


  const textRef = useRef<HTMLHeadingElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [orgDisplayName]);
  
  return (
    <TopNav>
      <AppBar position="static" className={"topNav"}>
        <Toolbar
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        >
          <div style={{ minWidth: 0, flex: 1, width: '0' }}>
            <Tooltip title={orgDisplayName} disableHoverListener={!isOverflowing}>
              <Typography
              component={"h3"}
              ref={textRef}
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                paddingRight: "20px",
              }}
              sx={{
                color: "var(--kyocera-dark-grey)",
                fontSize: "24px",
              }}
              >
                {orgDisplayName}
              </Typography>
            </Tooltip>
          </div>
  
          <div style={{ flexShrink: 0 }}>
            {/*
            <Button onClick={handleLanguageClick} className={"languageButton"}>
              {userProfile.locale
                ? APP_LANGUAGES[userProfile.locale].full
                : APP_LANGUAGES[userLanguage].full}
              <ArrowDropDownSharp />
            </Button>
            */}
            <IconButton 
              edge="start"
              aria-label="profile"
              onClick={handleProfileClick}
              sx={ { color: "var(--kyocera-white) !important", 
              padding: "5px !important", width: "40px", height: "40px",  
              borderRadius: "50% !important ",  '&:hover': { backgroundColor: 'var(--kyocera-blue) !important' }, 
              fontSize: "1.0em !important", background: "var(--kyocera-blue) !important" } }
              >{userInitials}
            </IconButton>
 
            <Menu
              id="profile-menu"
              anchorEl={profileMenuEl}
              className="customMenuStyles"
              keepMounted
              open={Boolean(profileMenuEl)}
              onClose={handleProfileClose}
              // getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}>
              <div
                style={{
                  fontWeight: "bold",
                  padding: "6px 16px",
                  textTransform: "capitalize",
                }}>
                {`${userProfile?.firstname} ${userProfile?.lastname}`}
              </div>
                <MenuItem onClick={handleSettings} className={"menuItem"} dense>
                  {t("menu_option_settings")}
                </MenuItem>
              
              {/*<MenuItem
                onClick={handleUserSettings}
                className={"menuItem"}
                dense>
                {t("menu_option_user_settings")}
              </MenuItem>*/}
              <MenuItem onClick={handleLogOut} className={"menuItem"} dense>
                {t("menu_option_logout")}
              </MenuItem>
            </Menu>
            {/*
            <Menu
              id="language-menu"
              anchorEl={languageMenuEl}
              keepMounted
              className="customMenuStyles"
              open={Boolean(languageMenuEl)}
              onClose={handleLanguageClose}
              // getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}>
              {Object.entries(APP_LANGUAGES).map((value) => (
                <MenuItem
                  className={"menuItem"}
                  onClick={() => {
                    const locale = value[1].code;
                    handleUpdateUserLanguage(locale);
                  }}
                  key={value[1].code}
                  dense>
                  {value[1].full}
                </MenuItem>
              ))}
            </Menu>
            */}
            
          </div>
        </Toolbar>
      </AppBar>
    </TopNav>
  );
};

export default TopNavComponent;
